import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { myFirebaseAuth } from '../../firebase/config';
import { logoutUser } from './authService';
import { PaymentPlanCreate, PaymentPlanUpdate } from '@dill/dill-shared';

// ---------------------------------------------------------------------------
// Supplier-side thunks (mounted under /suppliers/:supplierId/paymentPlans)
// ---------------------------------------------------------------------------

export const getBuyerPaymentPlans = createAsyncThunk(
  'paymentPlans/getBuyerPaymentPlans',
  async ({ supplierId, buyerId }: { supplierId: string; buyerId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/paymentPlans/buyer/${buyerId}/getBuyerPaymentPlans`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getSupplierPaymentPlans = createAsyncThunk(
  'paymentPlans/getSupplierPaymentPlans',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/paymentPlans/getSupplierPaymentPlans`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const createPaymentPlan = createAsyncThunk(
  'paymentPlans/createPaymentPlan',
  async ({ supplierId, data }: { supplierId: string; data: PaymentPlanCreate }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/paymentPlans/createPaymentPlan`,
        data,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data?.errors?.[0]?.msg || error.message);
    }
  }
);

export const updatePaymentPlan = createAsyncThunk(
  'paymentPlans/updatePaymentPlan',
  async (
    {
      supplierId,
      paymentPlanId,
      data,
    }: { supplierId: string; paymentPlanId: string; data: PaymentPlanUpdate },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/paymentPlans/${paymentPlanId}/updatePaymentPlan`,
        data,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const recordPaymentPlanPayment = createAsyncThunk(
  'paymentPlans/recordPaymentPlanPayment',
  async (
    {
      supplierId,
      paymentPlanId,
      amount,
    }: { supplierId: string; paymentPlanId: string; amount: number },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/paymentPlans/${paymentPlanId}/recordPayment`,
        { amount },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data?.errors?.[0]?.msg || error.message);
    }
  }
);

export const cancelPaymentPlan = createAsyncThunk(
  'paymentPlans/cancelPaymentPlan',
  async (
    { supplierId, paymentPlanId }: { supplierId: string; paymentPlanId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/paymentPlans/${paymentPlanId}/cancelPaymentPlan`,
        {},
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const deletePaymentPlan = createAsyncThunk(
  'paymentPlans/deletePaymentPlan',
  async (
    { supplierId, paymentPlanId }: { supplierId: string; paymentPlanId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.delete<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/paymentPlans/${paymentPlanId}/deletePaymentPlan`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

// ---------------------------------------------------------------------------
// Buyer-side thunks (mounted under /mainBuyers/:mainBuyerId/paymentPlans)
// ---------------------------------------------------------------------------

export const getMainBuyerPaymentPlans = createAsyncThunk(
  'paymentPlans/getMainBuyerPaymentPlans',
  async ({ mainBuyerId, buyerId }: { mainBuyerId: string; buyerId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/paymentPlans/getMainBuyerPaymentPlans?buyerId=${buyerId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

// Note: no createMainBuyerPaymentPlan / recordMainBuyerPaymentPlanPayment thunks - only a
// supplier can create a plan, and recording a manual payment is a supplier-only action too
// (see paymentPlanControllers.ts on the backend).

export const confirmMainBuyerPaymentPlan = createAsyncThunk(
  'paymentPlans/confirmMainBuyerPaymentPlan',
  async (
    {
      mainBuyerId,
      buyerId,
      paymentPlanId,
      paymentMethodId,
    }: { mainBuyerId: string; buyerId: string; paymentPlanId: string; paymentMethodId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/paymentPlans/${paymentPlanId}/confirmPaymentPlan`,
        { buyerId, paymentMethodId },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data?.errors?.[0]?.msg || error.message);
    }
  }
);

export const updateMainBuyerPaymentPlanPaymentMethod = createAsyncThunk(
  'paymentPlans/updateMainBuyerPaymentPlanPaymentMethod',
  async (
    {
      mainBuyerId,
      buyerId,
      paymentPlanId,
      paymentMethodId,
    }: { mainBuyerId: string; buyerId: string; paymentPlanId: string; paymentMethodId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/paymentPlans/${paymentPlanId}/updatePaymentMethod`,
        { buyerId, paymentMethodId },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data?.errors?.[0]?.msg || error.message);
    }
  }
);

export const cancelMainBuyerPaymentPlan = createAsyncThunk(
  'paymentPlans/cancelMainBuyerPaymentPlan',
  async (
    {
      mainBuyerId,
      buyerId,
      paymentPlanId,
    }: { mainBuyerId: string; buyerId: string; paymentPlanId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/paymentPlans/${paymentPlanId}/cancelPaymentPlan`,
        { buyerId },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
